import { useState, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Slider } from "../components/ui/slider";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { 
  Building2, Upload, PoundSterling, Percent, CreditCard,
  ChevronRight, ChevronLeft, Check, Loader2, FileText,
  Zap, Crown, TrendingUp, Sparkles, Gift, ArrowRight
} from "lucide-react";
import { toast } from "sonner";

const PLANS = [
  {
    id: "trial",
    name: "Free Trial",
    price: 0,
    period: "",
    quotes: 3,
    description: "Try before you buy",
    features: ["3 free quotes", "Full AI generation", "PDF export", "Email quotes"],
    icon: Gift,
    color: "text-green-500",
    bgColor: "bg-green-500/10",
    borderColor: "border-green-500/30",
    popular: false
  },
  {
    id: "starter",
    name: "Starter",
    price: 59,
    period: "/month",
    quotes: 25,
    description: "For growing businesses",
    features: ["25 quotes/month", "Priority support", "Custom branding", "All features"],
    icon: TrendingUp,
    color: "text-blue-500",
    bgColor: "bg-blue-500/10",
    borderColor: "border-blue-500/30",
    popular: false
  },
  {
    id: "professional",
    name: "Professional",
    price: 99,
    period: "/month",
    quotes: 75,
    description: "Most popular choice",
    features: ["75 quotes/month", "Priority support", "Custom branding", "Analytics"],
    icon: Crown,
    color: "text-primary",
    bgColor: "bg-primary/10",
    borderColor: "border-primary/50",
    popular: true
  },
  {
    id: "business",
    name: "Business",
    price: 150,
    period: "/month",
    quotes: 150,
    description: "For larger teams",
    features: ["150 quotes/month", "Dedicated support", "API access", "Team features"],
    icon: Sparkles,
    color: "text-purple-500",
    bgColor: "bg-purple-500/10",
    borderColor: "border-purple-500/30",
    popular: false
  }
];

const OnboardingWizard = ({ user, setUser, API, onComplete }) => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Form state
  const [companyName, setCompanyName] = useState(user?.company_name || "");
  const [logoUrl, setLogoUrl] = useState(user?.logo_url || null);
  const [hourlyRate, setHourlyRate] = useState(user?.hourly_rate || 50);
  const [marginPercentage, setMarginPercentage] = useState(user?.margin_percentage || 20);
  const [hasFirstHourPremium, setHasFirstHourPremium] = useState(user?.has_first_hour_premium || false);
  const [firstHourRate, setFirstHourRate] = useState(user?.first_hour_rate || 75);
  const [emergencyCalloutFee, setEmergencyCalloutFee] = useState(user?.emergency_callout_fee || 0);
  const [selectedPlan, setSelectedPlan] = useState("trial");
  
  // Legal consent state (for Google users who haven't accepted yet)
  const [agreedToTerms, setAgreedToTerms] = useState(!!user?.legal_consent_accepted);
  const needsLegalConsent = user?.auth_provider === "google" && !user?.legal_consent_accepted;
  
  const totalSteps = 5;
  
  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      const response = await fetch(`${API}/settings/logo`, {
        method: "POST",
        credentials: "include",
        body: formData
      });
      
      if (!response.ok) throw new Error("Failed to upload");
      
      const data = await response.json();
      setLogoUrl(data.logo_url);
      toast.success("Logo uploaded!");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };
  
  const saveSettings = async () => {
    try {
      const response = await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          company_name: companyName,
          hourly_rate: hourlyRate,
          margin_percentage: marginPercentage,
          has_first_hour_premium: hasFirstHourPremium,
          first_hour_rate: hasFirstHourPremium ? firstHourRate : null,
          emergency_callout_fee: emergencyCalloutFee
        })
      });
      
      if (response.ok) {
        const updated = await response.json();
        setUser(updated);
      }
    } catch (error) {
      console.error("Failed to save settings:", error);
    }
  };
  
  const handlePlanSelection = async () => {
    setSaving(true);
    
    try {
      // Save settings first
      await saveSettings();
      
      if (selectedPlan === "trial") {
        // Free trial - just activate and complete
        await fetch(`${API}/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            onboarding_completed: true,
            subscription_plan: "trial"
          })
        });
        
        toast.success("Welcome to Trade Office! You have 3 free quotes to get started.");
        onComplete();
      } else {
        // Paid plan - redirect to Stripe
        setProcessingPayment(true);
        
        const response = await fetch(`${API}/subscriptions/create-checkout`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            plan_id: selectedPlan,
            success_url: `${window.location.origin}/payment/success?session_id={CHECKOUT_SESSION_ID}&onboarding=true`,
            cancel_url: `${window.location.origin}/dashboard`
          })
        });
        
        if (!response.ok) {
          throw new Error("Failed to create checkout");
        }
        
        const data = await response.json();
        
        // Mark onboarding as completed before redirecting
        await fetch(`${API}/settings`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ onboarding_completed: true })
        });
        
        // Redirect to Stripe
        if (data.checkout_url) {
          window.location.href = data.checkout_url;
        }
      }
    } catch (error) {
      toast.error("Failed to process. Please try again.");
      setProcessingPayment(false);
    } finally {
      setSaving(false);
    }
  };
  
  const nextStep = async () => {
    if (step === 4) {
      // Save settings before plan selection
      await saveSettings();
    }
    
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };
  
  const prevStep = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };
  
  const skipOnboarding = async () => {
    try {
      await fetch(`${API}/settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          onboarding_completed: true,
          subscription_plan: "trial"
        })
      });
      toast.success("You can complete setup anytime in Settings");
      onComplete();
    } catch (error) {
      onComplete();
    }
  };
  
  const steps = [
    {
      title: "Welcome!",
      subtitle: "Let's set up your account in 2 minutes",
      icon: <FileText className="w-8 h-8" />
    },
    {
      title: "Company Details",
      subtitle: "Tell us about your business",
      icon: <Building2 className="w-8 h-8" />
    },
    {
      title: "Your Logo",
      subtitle: "Add your company logo for quotes",
      icon: <Upload className="w-8 h-8" />
    },
    {
      title: "Pricing Setup",
      subtitle: "Set your rates and margins",
      icon: <PoundSterling className="w-8 h-8" />
    },
    {
      title: "Choose Your Plan",
      subtitle: "Start free or subscribe for more",
      icon: <CreditCard className="w-8 h-8" />
    }
  ];
  
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Logo */}
        <div className="flex items-center justify-center gap-3 mb-6">
          <div className="w-10 h-10 bg-primary rounded-md flex items-center justify-center">
            <FileText className="w-6 h-6 text-primary-foreground" />
          </div>
          <span className="text-2xl font-black tracking-tight">Trade Office</span>
        </div>
        
        {/* Progress */}
        <div className="flex items-center justify-center gap-2 mb-6">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`h-2 rounded-full transition-all duration-300 ${
                index + 1 === step 
                  ? "w-8 bg-primary" 
                  : index + 1 < step 
                    ? "w-8 bg-primary/50" 
                    : "w-2 bg-secondary"
              }`}
            />
          ))}
        </div>
        
        {/* Card */}
        <Card className="bg-card border-white/5">
          <CardContent className="pt-6 pb-6">
            {/* Step 1: Welcome */}
            {step === 1 && (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Check className="w-10 h-10 text-primary" />
                </div>
                <h2 className="text-3xl font-bold mb-3">Welcome, {user?.name?.split(' ')[0] || 'there'}!</h2>
                <p className="text-muted-foreground text-lg mb-8 max-w-md mx-auto">
                  You're just a few steps away from creating professional quotes in seconds.
                </p>
                
                <div className="grid grid-cols-3 gap-4 max-w-md mx-auto mb-8">
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <Zap className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">AI-Powered</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <FileText className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">PDF Quotes</p>
                  </div>
                  <div className="text-center p-4 bg-secondary/30 rounded-lg">
                    <PoundSterling className="w-6 h-6 text-primary mx-auto mb-2" />
                    <p className="text-sm font-medium">Live Prices</p>
                  </div>
                </div>
                
                {/* Legal Consent for Google Users */}
                {needsLegalConsent && (
                  <div className="max-w-md mx-auto mb-6 text-left">
                    <div className="flex items-start gap-3 p-4 bg-secondary/20 rounded-lg border border-white/5">
                      <Checkbox
                        id="onboard-terms-consent"
                        checked={agreedToTerms}
                        onCheckedChange={async (checked) => {
                          setAgreedToTerms(checked);
                          if (checked) {
                            try {
                              await fetch(`${API}/auth/accept-terms`, {
                                method: "POST",
                                credentials: "include"
                              });
                            } catch (error) {
                              console.error("Failed to record consent:", error);
                            }
                          }
                        }}
                        className="mt-0.5"
                        data-testid="onboard-terms-consent-checkbox"
                      />
                      <div>
                        <Label 
                          htmlFor="onboard-terms-consent" 
                          className="text-sm leading-relaxed cursor-pointer"
                          style={{ fontSize: '12px' }}
                        >
                          I agree to the{' '}
                          <Link 
                            to="/terms" 
                            target="_blank"
                            className="text-orange-500 hover:text-orange-400 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Terms &amp; Conditions
                          </Link>
                          {' '}and{' '}
                          <Link 
                            to="/privacy" 
                            target="_blank"
                            className="text-orange-500 hover:text-orange-400 hover:underline font-medium"
                            onClick={(e) => e.stopPropagation()}
                          >
                            Privacy Policy
                          </Link>
                          {' '}for Trade Office Solutions Limited.
                        </Label>
                        <p className="text-xs text-muted-foreground mt-1.5">
                          Joining 100s of other plumbers who quote 5x faster.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                <Button
                  size="lg"
                  onClick={() => setStep(2)}
                  disabled={needsLegalConsent && !agreedToTerms}
                  className="bg-primary text-primary-foreground px-8"
                  data-testid="onboard-start"
                >
                  Let's Get Started
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                
                {needsLegalConsent && !agreedToTerms && (
                  <p className="text-xs text-muted-foreground mt-3">
                    Please agree to the terms to continue.
                  </p>
                )}
              </div>
            )}
            
            {/* Step 2: Company Name */}
            {step === 2 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                    <Building2 className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Company Details</h2>
                  <p className="text-muted-foreground">This will appear on your quotes</p>
                </div>
                
                <div className="space-y-4 max-w-sm mx-auto">
                  <div className="space-y-2">
                    <Label>Company Name</Label>
                    <Input
                      data-testid="onboard-company-name"
                      value={companyName}
                      onChange={(e) => setCompanyName(e.target.value)}
                      placeholder="e.g., Smith Plumbing Services"
                      className="h-12 text-lg bg-input/50 border-white/10"
                      autoFocus
                    />
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 3: Logo */}
            {step === 3 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                    <Upload className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Your Logo</h2>
                  <p className="text-muted-foreground">Add your company logo (optional)</p>
                </div>
                
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleLogoUpload}
                  accept="image/*"
                  className="hidden"
                />
                
                <div className="flex flex-col items-center gap-4">
                  <div className="w-32 h-32 bg-secondary rounded-xl flex items-center justify-center overflow-hidden border-2 border-dashed border-border">
                    {logoUrl ? (
                      <img 
                        src={logoUrl} 
                        alt="Logo" 
                        className="w-full h-full object-contain p-2"
                      />
                    ) : (
                      <Upload className="w-10 h-10 text-muted-foreground" />
                    )}
                  </div>
                  
                  <Button
                    variant="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="onboard-upload-logo"
                  >
                    {uploading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Uploading...
                      </>
                    ) : logoUrl ? (
                      "Change Logo"
                    ) : (
                      "Upload Logo"
                    )}
                  </Button>
                  
                  <p className="text-sm text-muted-foreground">
                    PNG or JPG, max 2MB
                  </p>
                </div>
              </div>
            )}
            
            {/* Step 4: Pricing */}
            {step === 4 && (
              <div className="space-y-6">
                <div className="text-center mb-6">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                    <PoundSterling className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Set Your Rates</h2>
                  <p className="text-muted-foreground">You can change these anytime</p>
                </div>
                
                <div className="space-y-8 max-w-md mx-auto">
                  {/* Standard Hourly Rate */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label>Standard Hourly Rate</Label>
                      <span className="text-2xl font-bold text-primary mono">£{hourlyRate}/hr</span>
                    </div>
                    <Slider
                      value={[hourlyRate]}
                      onValueChange={(v) => setHourlyRate(v[0])}
                      min={10}
                      max={200}
                      step={5}
                      className="w-full"
                      data-testid="onboard-hourly-rate"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>£10</span>
                      <span>£200</span>
                    </div>
                  </div>

                  {/* First Hour Premium */}
                  <div className="space-y-4 p-4 bg-secondary/20 rounded-lg border border-white/10">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base">Charge more for first hour?</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Covers travel time to the job
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setHasFirstHourPremium(!hasFirstHourPremium)}
                        className={`w-12 h-6 rounded-full transition-colors ${hasFirstHourPremium ? 'bg-primary' : 'bg-muted'} relative`}
                        data-testid="first-hour-toggle"
                      >
                        <span className={`block w-5 h-5 bg-white rounded-full absolute top-0.5 transition-transform ${hasFirstHourPremium ? 'translate-x-6' : 'translate-x-0.5'}`} />
                      </button>
                    </div>
                    
                    {hasFirstHourPremium && (
                      <div className="space-y-3 pt-2">
                        <div className="flex justify-between items-center">
                          <Label className="text-sm">First Hour Rate</Label>
                          <span className="text-xl font-bold text-primary mono">£{firstHourRate}/hr</span>
                        </div>
                        <Slider
                          value={[firstHourRate]}
                          onValueChange={(v) => setFirstHourRate(v[0])}
                          min={hourlyRate}
                          max={300}
                          step={5}
                          className="w-full"
                          data-testid="first-hour-rate"
                        />
                        <p className="text-xs text-muted-foreground">
                          First hour: £{firstHourRate}, then £{hourlyRate}/hr after
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Emergency Callout Fee */}
                  <div className="space-y-4 p-4 bg-secondary/20 rounded-lg border border-white/10">
                    <div className="flex justify-between items-center">
                      <div>
                        <Label className="text-base">Emergency Callout Fee</Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Applied when quote mentions "emergency"
                        </p>
                      </div>
                      <span className="text-xl font-bold text-primary mono">£{emergencyCalloutFee}</span>
                    </div>
                    <Slider
                      value={[emergencyCalloutFee]}
                      onValueChange={(v) => setEmergencyCalloutFee(v[0])}
                      min={0}
                      max={200}
                      step={10}
                      className="w-full"
                      data-testid="emergency-fee"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>£0 (off)</span>
                      <span>£200</span>
                    </div>
                  </div>
                  
                  {/* Margin */}
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <Label>Profit Margin</Label>
                      <span className="text-2xl font-bold text-primary mono">{marginPercentage}%</span>
                    </div>
                    <Slider
                      value={[marginPercentage]}
                      onValueChange={(v) => setMarginPercentage(v[0])}
                      min={0}
                      max={100}
                      step={5}
                      className="w-full"
                      data-testid="onboard-margin"
                    />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>0%</span>
                      <span>100%</span>
                    </div>
                  </div>
                  
                  <div className="p-3 bg-secondary/30 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">
                      Example: £100 job → <span className="text-primary font-bold">£{(100 * (1 + marginPercentage / 100)).toFixed(0)}</span> quote
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Step 5: Plan Selection */}
            {step === 5 && (
              <div className="space-y-6">
                <div className="text-center mb-4">
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4 text-primary">
                    <CreditCard className="w-7 h-7" />
                  </div>
                  <h2 className="text-2xl font-bold mb-2">Choose Your Plan</h2>
                  <p className="text-muted-foreground">Start free, upgrade anytime</p>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  {PLANS.map((plan) => {
                    const Icon = plan.icon;
                    const isSelected = selectedPlan === plan.id;
                    
                    return (
                      <button
                        key={plan.id}
                        onClick={() => setSelectedPlan(plan.id)}
                        className={`relative p-4 rounded-xl border-2 text-left transition-all ${
                          isSelected 
                            ? `${plan.borderColor} ${plan.bgColor}` 
                            : "border-white/10 hover:border-white/20"
                        }`}
                        data-testid={`plan-${plan.id}`}
                      >
                        {plan.popular && (
                          <Badge className="absolute -top-2 right-2 bg-primary text-primary-foreground text-xs">
                            Popular
                          </Badge>
                        )}
                        
                        <div className="flex items-center gap-2 mb-2">
                          <Icon className={`w-5 h-5 ${plan.color}`} />
                          <span className="font-semibold">{plan.name}</span>
                        </div>
                        
                        <div className="mb-2">
                          <span className="text-2xl font-bold">
                            {plan.price === 0 ? "Free" : `£${plan.price}`}
                          </span>
                          {plan.period && (
                            <span className="text-sm text-muted-foreground">{plan.period}</span>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground mb-2">
                          {plan.quotes} quotes{plan.id !== "trial" ? "/month" : ""}
                        </p>
                        
                        {isSelected && (
                          <div className="absolute top-2 right-2">
                            <Check className={`w-5 h-5 ${plan.color}`} />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
                
                <div className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">
                    {selectedPlan === "trial" 
                      ? "No credit card required. Start generating quotes immediately."
                      : "Secure payment via Stripe. Cancel anytime."}
                  </p>
                </div>
              </div>
            )}
            
            {/* Navigation */}
            {step > 1 && (
              <div className="flex items-center justify-between mt-8 pt-6 border-t border-border">
                <Button
                  variant="ghost"
                  onClick={prevStep}
                  disabled={saving || processingPayment}
                  data-testid="onboard-back"
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                
                {step < totalSteps ? (
                  <Button
                    onClick={nextStep}
                    disabled={step === 2 && !companyName.trim()}
                    className="bg-primary text-primary-foreground min-w-[120px]"
                    data-testid="onboard-continue"
                  >
                    {step === 3 && !logoUrl ? "Skip" : "Continue"}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                ) : (
                  <Button
                    onClick={handlePlanSelection}
                    disabled={saving || processingPayment}
                    className="bg-primary text-primary-foreground min-w-[140px]"
                    data-testid="onboard-finish"
                  >
                    {saving || processingPayment ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : selectedPlan === "trial" ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Start Free Trial
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Skip link */}
        {step > 1 && step < 5 && (
          <p className="text-center mt-4">
            <button
              onClick={skipOnboarding}
              className="text-sm text-muted-foreground hover:text-foreground transition-colors"
              data-testid="onboard-skip"
            >
              Skip setup for now
            </button>
          </p>
        )}
        
        {/* Step indicator */}
        <p className="text-center text-sm text-muted-foreground mt-4">
          Step {step} of {totalSteps}
        </p>
      </div>
    </div>
  );
};

export default OnboardingWizard;
